import { ChannelDefinition, IConnectionStatus } from "../transport/types";
import { PublishOptions, WireMsg, WireType } from "../types";

export type OnNewLatestCallback<D extends WireMsg> = (msg: Readonly<D>) => void;
export type OnNewHistoryCallback<D extends WireMsg> = (msg: Readonly<D[]>) => void;
export interface IChannelInfo<D extends WireMsg> {
  history: D[];
  onNewLatest: OnNewLatestCallback<D>;
}

export default abstract class SkybusProviderBase implements IConnectionStatus {
  protected channels: Map<string, IChannelInfo<WireMsg>> = new Map();
  public getChannelInfo(channel: string) {
    return this.channels.get(channel);
  }
  public abstract subscribe<D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    onNewLatest: OnNewLatestCallback<D>,
    onNewHistory: OnNewHistoryCallback<D>
  ): void;

  /**
   * Subscribe is synchronous and doesn't indicate when the subscription has been
   * accepted by the vehicle. genSubscription is async and needs to resolve ONLY once
   * the vehicle has either accepted or rejected the requested subscription
   */
  public abstract genSubscription<D extends WireMsg>(
    channel: string,
    type: WireType<D>,
    onNewLatest: OnNewLatestCallback<D>,
    onNewHistory: OnNewHistoryCallback<D>,
    debugKey?: string
  ): Promise<void>;

  public abstract unsubscribe(channel: string): void;

  public abstract publish<D extends WireMsg>(
    channel: string | ChannelDefinition<WireType<D>>,
    msg: D,
    options?: PublishOptions
  ): Promise<void>;

  public abstract isConnected(): boolean;

  public abstract addConnectionStatusListener(callback: (connected: boolean) => void): () => void;
}
